/**
 * AuthPage - Full authentication page
 * Supports Email signup/login and Google OAuth
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Mail, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, isAuthenticated } = useAuth();
  
  const [mode, setMode] = useState('signup'); // 'signup', 'login'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Get redirect URL from state or default to home
  const from = location.state?.from?.pathname || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Check for OAuth callback
  useEffect(() => {
    const provider = searchParams.get('provider');
    if (provider === 'google') {
      // Handle Google OAuth - in production this would process the callback
      console.log('Google OAuth callback');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const cleanEmail = email.trim();
    // Our own validation (iOS Safari's native email validation throws a confusing
    // "The string did not match the expected pattern" — we bypass it via noValidate).
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail);
    if (!emailOk) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setLoading(true);

    try {
      let result;
      if (mode === 'signup') {
        if (!name.trim()) {
          setError('Please enter your name');
          setLoading(false);
          return;
        }
        result = await register(name.trim(), cleanEmail, password);
      } else {
        result = await login(cleanEmail, password);
      }

      if (result.success) {
        navigate(from, { replace: true });
      } else {
        // Handle error - could be a string or an object
        let errorMessage = 'Authentication failed';
        if (typeof result.error === 'string') {
          errorMessage = result.error;
        } else if (result.error?.detail) {
          // FastAPI validation error format
          if (Array.isArray(result.error.detail)) {
            errorMessage = result.error.detail.map(e => e.msg || e).join(', ');
          } else if (typeof result.error.detail === 'string') {
            errorMessage = result.error.detail;
          }
        } else if (result.error?.msg) {
          errorMessage = result.error.msg;
        }
        setError(errorMessage);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get the Google OAuth URL from the backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/url`, {
        headers: {
          'Origin': window.location.origin,
          'Referer': window.location.href
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to initialize Google sign in');
      }
      
      const data = await response.json();
      
      if (data.url) {
        // Redirect to Google OAuth consent screen
        window.location.href = data.url;
      } else {
        throw new Error('No OAuth URL received');
      }
    } catch (err) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google sign in is not available right now. Please use email signup.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <img
              src="/nlt-radio-logo.png"
              alt="NLT Radio"
              className="w-28 h-28 object-contain mb-3 drop-shadow-[0_0_25px_rgba(0,240,255,0.25)]"
              data-testid="auth-logo"
            />
            <p className="text-[#A1A1AA] text-sm mt-1">
              {mode === 'signup' ? 'Create your account' : 'Welcome back'}
            </p>
          </div>

          {/* Google Button */}
          <button
            onClick={handleGoogleAuth}
            className="w-full py-3 px-4 rounded-xl bg-white text-[#050505] font-medium flex items-center justify-center gap-3 hover:bg-white/90 transition-colors mb-4"
            data-testid="google-auth-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[#52525B] text-sm">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {mode === 'signup' && (
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="name-input"
                />
              </div>
            )}

            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Email</label>
              <input
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  className="w-full px-4 py-3 pr-12 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF] transition-colors"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2.5 text-[#52525B] hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {mode === 'login' && (
                <div className="text-right mt-2">
                  <a
                    href="/forgot-password"
                    className="text-[#00F0FF] text-sm hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </a>
                </div>
              )}
            </div>

            {error && (
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-[#EF4444] text-sm"
              >
                {error}
              </motion.p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 rounded-xl bg-[#00F0FF] text-[#050505] font-semibold hover:bg-[#00F0FF]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <motion.span
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-5 h-5 border-2 border-[#050505] border-t-transparent rounded-full"
                  />
                  {mode === 'signup' ? 'Creating account...' : 'Logging in...'}
                </span>
              ) : (
                mode === 'signup' ? 'Create Account' : 'Log In'
              )}
            </button>
          </form>

          {/* Toggle Mode */}
          <p className="mt-6 text-center text-[#A1A1AA] text-sm">
            {mode === 'signup' ? (
              <>
                Already have an account?{' '}
                <button 
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-[#00F0FF] hover:underline font-medium py-2 px-1 inline-block"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button 
                  onClick={() => { setMode('signup'); setError(''); }}
                  className="text-[#00F0FF] hover:underline font-medium py-2 px-1 inline-block"
                >
                  Sign up
                </button>
              </>
            )}
          </p>

          {/* Terms */}
          <p className="mt-6 text-center text-[#52525B] text-xs">
            By continuing, you agree to our{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#00F0FF] hover:underline" data-testid="auth-terms-link">Terms of Service</a>
            {' '}and Acceptable Use Policy.
            <br />
            <span className="text-[#A1A1AA]">Your account works on both NLT Radio and NoLiesTold.</span>
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default AuthPage;
